import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios'
import type { RuntimeConfig } from '@/core/config/runtime'
import { ApiError } from '@/core/errors'
import { loadSession } from '@/core/storage/session'

export type ApiMessageType = 'success' | 'warning' | 'error'

export interface ApiResult<T> {
  status?: number
  code: number
  msg?: string
  data?: T
  timestamp?: string
  traceId?: string
  type?: string
  level?: string
  msgType?: string
  messageType?: string
}

export interface DownloadResult {
  blob: Blob
  filename?: string
  contentType?: string
}

export interface HttpRequestOptions extends AxiosRequestConfig {
  skipLoading?: boolean
}

interface HttpHooks {
  onUnauthorized?: () => void
  onLoadingChange?: (delta: 1 | -1) => void
}

type HttpChannel = 'api' | 'upload' | 'download'

let clients: Partial<Record<HttpChannel, AxiosInstance>> = {}
let hooks: HttpHooks = {}
let unauthorizedHandled = false
let accessToken = ''
let accessTokenName = 'satoken'

export function isApiResult<T>(value: unknown): value is ApiResult<T> {
  return Boolean(
    value &&
      typeof value === 'object' &&
      'code' in value &&
      ('data' in value || 'msg' in value || 'status' in value)
  )
}

export function resolveApiMessageType(
  result: Pick<ApiResult<unknown>, 'code' | 'type' | 'level' | 'msgType' | 'messageType'>
) {
  const declaredType = [result.messageType, result.msgType, result.type, result.level]
    .find((value) => typeof value === 'string' && value.trim())
    ?.trim()
    .toLowerCase()

  if (declaredType && ['warning', 'warn', '警告', '提示'].includes(declaredType)) return 'warning'
  if (declaredType && ['success', '成功'].includes(declaredType)) return 'success'
  if (declaredType && ['error', 'danger', 'fail', 'failed', 'failure', '失败'].includes(declaredType)) return 'error'
  if (result.code === 1 || result.code === 200) return 'success'
  if (result.code === 2) return 'warning'
  return 'error'
}

function notifyUnauthorized() {
  if (unauthorizedHandled) return
  unauthorizedHandled = true
  hooks.onUnauthorized?.()
}

export function configureHttp(config: RuntimeConfig, nextHooks: HttpHooks = {}) {
  hooks = nextHooks
  unauthorizedHandled = false
  clients = {
    api: createClient(config.apiBaseUrl, config.requestTimeout),
    upload: createClient(config.uploadBaseUrl, config.requestTimeout),
    download: createClient(config.downloadBaseUrl, config.requestTimeout)
  }
}

export function setHttpAccessToken(token?: string, tokenName = 'satoken') {
  accessToken = token?.trim() || ''
  accessTokenName = tokenName.trim() || 'satoken'
}

export function resetHttpSessionState() {
  unauthorizedHandled = false
}

function createClient(baseURL: string, timeout: number) {
  const instance = axios.create({ baseURL, timeout })
  instance.interceptors.request.use(
    (request) => {
      const session = loadSession()
      const token = accessToken || session?.tokenValue
      const tokenName = accessToken ? accessTokenName : session?.tokenName || 'satoken'
      request.headers.set('Call-Source', 'WEB')
      request.headers.set('Api-Version', '1.0')
      if (token) request.headers.set(tokenName, token)
      if (!(request as HttpRequestOptions).skipLoading) hooks.onLoadingChange?.(1)
      return request
    },
    (error) => Promise.reject(toApiError(error))
  )
  instance.interceptors.response.use(
    (response) => {
      if (!(response.config as HttpRequestOptions).skipLoading) hooks.onLoadingChange?.(-1)
      return response
    },
    async (error) => {
      if (!(error?.config as HttpRequestOptions | undefined)?.skipLoading) hooks.onLoadingChange?.(-1)
      if (error?.response?.status === 401) notifyUnauthorized()
      return Promise.reject(await toResponseApiError(error))
    }
  )
  return instance
}

async function readBlobText(blob: Blob) {
  if (typeof blob.text === 'function') return blob.text()
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || ''))
    reader.onerror = () => reject(reader.error || new Error('请求响应读取失败'))
    reader.readAsText(blob)
  })
}

async function parseErrorPayload(payload: unknown) {
  if (typeof Blob === 'undefined' || !(payload instanceof Blob)) return payload
  try {
    return JSON.parse(await readBlobText(payload)) as unknown
  } catch {
    return payload
  }
}

async function toResponseApiError(error: unknown) {
  if (!axios.isAxiosError(error)) return toApiError(error)
  const payload = await parseErrorPayload(error.response?.data)
  const status = error.response?.status
  const message =
    payload && typeof payload === 'object' && 'msg' in payload && typeof payload.msg === 'string'
      ? payload.msg
      : error.message || '请求失败'
  const code =
    payload && typeof payload === 'object' && 'code' in payload && typeof payload.code === 'number'
      ? payload.code
      : undefined
  return new ApiError(message, { code, status, details: payload || error })
}

function toApiError(error: unknown) {
  if (error instanceof ApiError) return error
  if (error instanceof AxiosError) {
    const status = error.response?.status
    const payload = error.response?.data
    const message = typeof payload?.msg === 'string' ? payload.msg : error.message || '请求失败'
    return new ApiError(message, { status, details: payload || error })
  }
  return error
}

function requireClient(channel: HttpChannel = 'api') {
  const instance = clients[channel]
  if (!instance) throw new Error('HTTP client has not been configured')
  return instance
}

export async function requestResult<T>(
  config: HttpRequestOptions,
  channel: HttpChannel = 'api'
): Promise<ApiResult<T>> {
  const response = await requireClient(channel).request<T | ApiResult<T>>(config)
  const payload = response.data
  if (isApiResult<T>(payload)) {
    if (payload.code !== 1 && payload.code !== 200) {
      if (payload.code === 401 || payload.code === 20001) notifyUnauthorized()
      throw new ApiError(payload.msg || '请求失败', { code: payload.code, status: payload.status, details: payload })
    }
    return payload
  }
  return { code: 1, data: payload as T }
}

export async function request<T>(config: HttpRequestOptions, channel: HttpChannel = 'api'): Promise<T> {
  const result = await requestResult<T>(config, channel)
  return result.data as T
}

export async function requestRaw<T = unknown>(
  config: HttpRequestOptions,
  channel: HttpChannel = 'api'
): Promise<AxiosResponse<T>> {
  return requireClient(channel).request<T>(config)
}

function jsonHeaders(data: unknown, headers?: AxiosRequestConfig['headers']) {
  const isFormData = typeof FormData !== 'undefined' && data instanceof FormData
  if (isFormData) return headers
  return { 'Content-Type': 'application/json;charset=utf-8', ...(headers || {}) }
}

export function parseDownloadFilename(contentDisposition?: string) {
  if (!contentDisposition) return undefined
  const decodeFilename = (value: string) => {
    const filename = value.trim().replace(/^"|"$/g, '')
    try {
      return decodeURIComponent(filename)
    } catch {
      return filename
    }
  }
  const encoded = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i)?.[1]
  if (encoded) return decodeFilename(encoded)
  const filename =
    contentDisposition.match(/filename\s*=\s*"([^"]+)"/i)?.[1] ||
    contentDisposition.match(/filename\s*=\s*([^;]+)/i)?.[1]?.trim()
  return filename ? decodeFilename(filename) : undefined
}

export const http = {
  get<T>(url: string, params?: unknown, options: HttpRequestOptions = {}) {
    return request<T>({ ...options, method: 'GET', url, params })
  },
  post<T>(url: string, data?: unknown, options: HttpRequestOptions = {}) {
    return request<T>({ ...options, method: 'POST', url, data, headers: jsonHeaders(data, options.headers) })
  },
  put<T>(url: string, data?: unknown, options: HttpRequestOptions = {}) {
    return request<T>({ ...options, method: 'PUT', url, data, headers: jsonHeaders(data, options.headers) })
  },
  delete<T>(url: string, options: HttpRequestOptions = {}) {
    return request<T>({ ...options, method: 'DELETE', url })
  },
  async download(url: string, params?: unknown, options: HttpRequestOptions = {}): Promise<DownloadResult> {
    const response = await requestRaw<Blob>(
      { ...options, method: 'GET', url, params, responseType: 'blob' },
      'download'
    )
    return {
      blob: response.data,
      filename: parseDownloadFilename(response.headers['content-disposition']),
      contentType: typeof response.headers['content-type'] === 'string' ? response.headers['content-type'] : undefined
    }
  },
  uploadPost<T>(url: string, data?: unknown, options: HttpRequestOptions = {}) {
    return request<T>({ ...options, method: 'POST', url, data }, 'upload')
  },
  uploadGet<T>(url: string, params?: unknown, options: HttpRequestOptions = {}) {
    return request<T>({ ...options, method: 'GET', url, params }, 'upload')
  },
  uploadPostResult<T>(url: string, data?: unknown, options: HttpRequestOptions = {}) {
    return requestResult<T>({ ...options, method: 'POST', url, data }, 'upload')
  }
}
